import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom, startWith, switchMap} from 'rxjs';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ElciaCard} from '../../../../components/card/card';
import {TextInput} from '../../../../components/text-input/text-input';
import {EmailAutocomplete, EmailOption} from '../../../../components/email-autocomplete/email-autocomplete';
import {Dropdown} from '../../../../components/dropdown/dropdown';
import {Editor} from '../../../../components/editor/editor';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {ActivatedRoute, Router} from '@angular/router';
import {CompanyService, MailTemplateService, MailTemplateSummary, PartySelect, PartyType} from '../../../../../api';
import {PartyResource} from '../../../../../api/resources';
import {QueryKey} from '../../../../constants/query-key';
import {RouteUri} from '../../../../constants/route-uri';
import {InvoiceModelForm} from '../invoice-form/invoice-form';
import {InvoiceStore} from '../../services/invoice-store';
import {DocumentUploader} from '../../../../components/document-uploader/document-uploader';
import {emailArrayValidator} from '../../../../utils/form.utils';

export interface InvoiceSendForm {
  templateId: FormControl<number | null>;
  to: FormControl<string[] | null>;
  cc: FormControl<string[] | null>;
  bcc: FormControl<string[] | null>;
  object: FormControl<string | null>;
  content: FormControl<string | null>;
  isProForma: FormControl<boolean>;
  attachments: FormControl<File[]>;
}

@Component({
  selector: 'elcia-invoice-send',
  imports: [TranslatePipe, ReactiveFormsModule, ElciaCard, TextInput, EmailAutocomplete, Dropdown, Editor, ToggleSwitch, DocumentUploader],
  templateUrl: './invoice-send.html',
  styleUrl: './invoice-send.scss'
})
export class InvoiceSend {
  private readonly mailTemplateService = inject(MailTemplateService);
  private readonly companyService = inject(CompanyService);
  private readonly invoiceStore = inject(InvoiceStore);
  private readonly partyResourceService = inject(PartyResource);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly fg = input.required<FormGroup<InvoiceModelForm>>();
  readonly isSending = computed(() => this.invoiceStore.sendMutation.isPending());

  // Party types match invoice-edit usage so the customer can be resolved from the same list.
  private readonly partyTypes = signal<PartyType[]>([PartyType.Contact, PartyType.Company]);
  private readonly partyGetResource = this.partyResourceService.getParties(this.partyTypes);
  private readonly parties = computed<PartySelect[]>(() => this.partyGetResource.value()?.parties ?? []);

  private readonly fg$ = toObservable(this.fg);
  private readonly customerId = toSignal(
    this.fg$.pipe(switchMap(fg => fg.controls.customerId.valueChanges.pipe(startWith(fg.controls.customerId.value)))),
    {initialValue: null},
  );
  private readonly customer = computed<PartySelect | null>(
    () => this.parties().find(p => p.id === this.customerId()) ?? null,
  );

  // Si le client est une société, on charge ses détails pour proposer aussi ses contacts.
  private readonly companyId = computed(() => {
    const customer = this.customer();
    return customer?.type === PartyType.Company ? customer.id : null;
  });
  private readonly companyDetailsQuery = injectQuery(() => ({
    queryKey: [QueryKey.COMPANY, this.companyId()],
    queryFn: () => lastValueFrom(this.companyService.getCompanyById(this.companyId()!)),
    enabled: this.companyId() != null,
  }));

  // Suggestions d'emails (libellé "fullName (email)") : la société + ses contacts, ou juste le contact.
  protected readonly emailSuggestions = computed<EmailOption[]>(() => {
    const customer = this.customer();
    if (!customer) return [];

    const seen = new Set<string>();
    const options: EmailOption[] = [];
    const add = (name: string, email?: string | null) => {
      if (email && !seen.has(email)) {
        seen.add(email);
        options.push({label: `${name} (${email})`, email});
      }
    };

    if (customer.type === PartyType.Company) {
      const company = this.companyDetailsQuery.data()?.company;
      if (company) {
        add(company.name, company.email);
        company.contacts.forEach(contact => add(contact.fullName, contact.email));
        return options;
      }
    }

    // Contact (ou société pas encore chargée) : juste l'email du client.
    add(customer.fullName, customer.email);
    return options;
  });

  protected readonly sendForm = new FormGroup<InvoiceSendForm>({
    templateId: new FormControl<number | null>(null),
    to: new FormControl<string[] | null>(null, [Validators.required, emailArrayValidator()]),
    cc: new FormControl<string[] | null>(null, emailArrayValidator()),
    bcc: new FormControl<string[] | null>(null, emailArrayValidator()),
    object: new FormControl<string | null>(null, Validators.required),
    content: new FormControl<string | null>(null, Validators.required),
    isProForma: new FormControl<boolean>(false, {nonNullable: true}),
    attachments: new FormControl<File[]>([], {nonNullable: true}),
  });

  private readonly invoiceId = this.route.snapshot.paramMap.has('id')
    ? Number(this.route.snapshot.paramMap.get('id'))
    : null;

  private readonly mailTemplatesQuery = injectQuery(() => ({
    queryKey: [QueryKey.MAIL_TEMPLATES],
    queryFn: () => lastValueFrom(this.mailTemplateService.getMailTemplates(null)),
  }));

  protected readonly mailTemplates = computed<MailTemplateSummary[]>(
    () => this.mailTemplatesQuery.data()?.mailTemplates ?? [],
  );

  private readonly selectedTemplateId = signal<number | null>(null);

  private readonly templateDetailQuery = injectQuery(() => ({
    queryKey: [QueryKey.MAIL_TEMPLATE, this.selectedTemplateId()],
    queryFn: () => lastValueFrom(this.mailTemplateService.getMailTemplateById(this.selectedTemplateId()!)),
    enabled: this.selectedTemplateId() != null,
  }));

  private appliedTemplateId: number | null = null;
  private readonly applyTemplate = effect(() => {
    const template = this.templateDetailQuery.data()?.mailTemplate;
    if (!template || template.id === this.appliedTemplateId) return;
    this.appliedTemplateId = template.id;
    this.sendForm.patchValue({object: template.object, content: template.body});
  });

  // Pré-remplit To avec l'email principal du client (société ou contact), tant que l'utilisateur
  // n'a pas édité le champ. Pour une société, on attend ses détails pour disposer de son email.
  private toPrefilled = false;
  private readonly prefillRecipient = effect(() => {
    if (this.toPrefilled) return;

    const customer = this.customer();
    if (!customer) return;
    if (customer.type === PartyType.Company && !this.companyDetailsQuery.data()) return;

    const email = this.emailSuggestions()[0]?.email;
    if (!email) return;

    const toControl = this.sendForm.controls.to;
    if (toControl.dirty || (toControl.value?.length ?? 0) > 0) return;

    toControl.setValue([email]);
    this.toPrefilled = true;
  });

  protected addAttachments(files: File[]): void {
    this.sendForm.controls.attachments.setValue([...this.sendForm.controls.attachments.value, ...files]);
  }

  protected removeAttachment(index: number): void {
    this.sendForm.controls.attachments.setValue(
      this.sendForm.controls.attachments.value.filter((_, i) => i !== index),
    );
  }

  protected onTemplateSelected(templateId: unknown) {
    this.selectedTemplateId.set(templateId as number | null);
  }

  public send(): void {
    if (this.sendForm.invalid) {
      this.sendForm.markAllAsTouched();
      return;
    }
    if (this.invoiceId == null) return;

    const value = this.sendForm.getRawValue();
    this.invoiceStore.sendMutation.mutate({
      id: this.invoiceId,
      params: {
        to: value.to ?? [],
        object: value.object!,
        content: value.content!,
        isProForma: value.isProForma,
        cc: value.cc ?? [],
        bcc: value.bcc ?? [],
        attachments: value.attachments,
      },
    }, {
      onSuccess: () => this.router.navigate([RouteUri.FINANCES]),
    });
  }
}
